import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { siteContent, type Language, type SiteCopy } from "../content/siteContent";
import { track } from "../lib/analytics";

type LanguageContextValue = {
  language: Language;
  copy: SiteCopy;
  setLanguage: (language: Language) => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function getInitialLanguage(): Language {
  const stored = localStorage.getItem("luma-language");
  if (stored === "en" || stored === "zh") return stored;
  const browserLanguage = navigator.languages?.[0] ?? navigator.language;
  return /^(zh-CN|zh-SG|zh-Hans)/i.test(browserLanguage) ? "zh" : "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = siteContent[language].locale;
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    copy: siteContent[language],
    setLanguage(next) {
      setLanguageState(next);
      localStorage.setItem("luma-language", next);
      track("language_switch", { language: next });
    },
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
